import { formatNumber } from '../../utils/format';

/** Contacts left out of a flow, by the send guard's reason code. */
export type ExcludedCounts = Record<string, number>;

/** What `POST /flows/{id}/contacts/preview` says the bulk add would do. */
export interface FlowContactsPreview {
  selected_contacts: number;
  eligible_contacts: number;
  excluded: ExcludedCounts;
  strategies: Record<string, number>;
  /** Per channel, why it is closed for the skipped contacts. */
  excluded_by_channel?: Record<string, ExcludedCounts>;
}

/** What `POST /flows/{id}/contacts/bulk` did, as `useFlowContacts` returns it. */
export interface BulkAddResult {
  success: boolean;
  created?: number;
  skipped_duplicate?: number;
  errors?: number;
  excluded?: ExcludedCounts;
  error?: string;
}

// The send guard's reason codes (remi-api contact_guard / enrollment_plan).
const EXCLUSION_REASON_LABELS: Record<string, string> = {
  missing_phone: 'No phone number',
  invalid_phone: 'Invalid phone number',
  missing_email: 'No email address',
  email_opt_out: 'Opted out of email',
  email_spam_reported: 'Reported email as spam',
  email_bounced: 'Email bounced',
  sms_opt_out: 'Opted out of SMS',
  global_optout: 'Opted out of all contact',
  consent_blocked: 'Not opted in',
  consent_ladder: 'Not allowed by the consent ladder',
  channel_unavailable: 'Not available for this flow',
  dnc_tagged: 'Do not contact',
  dnc_phone_status: 'Phone on do-not-call list',
  legal_suppression: 'Legally suppressed',
  flywheel_opt_out: 'Opted out of Flywheel',
  ai_paused: 'AI paused for this contact',
  pending_handoff: 'Waiting on an agent handoff',
  missing_contact: 'Contact not found',
  contact_not_found: 'Contact not found',
};

/** A person-readable label for a reason code; unknown codes are humanized. */
export function exclusionReasonLabel(code: string): string {
  const known = EXCLUSION_REASON_LABELS[code];
  if (known) return known;
  const words = code.replace(/_/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Other';
}

/** Reasons with a count, largest first, labels merged where codes share one. */
export function excludedReasons(excluded: ExcludedCounts | undefined) {
  const byLabel = new Map<string, number>();
  for (const [code, count] of Object.entries(excluded ?? {})) {
    if (!count) continue;
    const label = exclusionReasonLabel(code);
    byLabel.set(label, (byLabel.get(label) ?? 0) + count);
  }
  return [...byLabel.entries()]
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

// The plan's channel names, in the order the guard checks them.
const CHANNEL_LABELS: [string, string][] = [
  ['voice', 'Calls'],
  ['sms', 'SMS'],
  ['email', 'Email'],
];

/**
 * Why each channel is closed for the skipped contacts, e.g.
 * "Calls: not opted in · SMS: not available for this flow". A contact is
 * skipped only when every channel the flow uses is closed, so the summary's
 * single reason is just the first of these.
 */
export function excludedByChannel(
  byChannel: Record<string, ExcludedCounts> | undefined
): { key: string; channel: string; reasons: { label: string; count: number }[] }[] {
  return CHANNEL_LABELS.filter(([key]) => byChannel?.[key])
    .map(([key, channel]) => ({ key, channel, reasons: excludedReasons(byChannel![key]) }))
    .filter((c) => c.reasons.length > 0);
}

export function excludedTotal(excluded: ExcludedCounts | undefined): number {
  return Object.values(excluded ?? {}).reduce((sum, n) => sum + (n || 0), 0);
}

/**
 * Whether Add to Flow can run: only on a preview of the current Search that has
 * finished loading and found someone the flow can reach. A preview that is
 * refreshing gates Add, so a corrected consent is never blocked by an old zero.
 */
export function canAddToFlow({
  searched,
  previewFetching,
  preview,
}: {
  searched: boolean;
  previewFetching: boolean;
  preview: Pick<FlowContactsPreview, 'eligible_contacts'> | null;
}): { allowed: boolean; reason?: 'search-first' | 'none-addable' } {
  if (!searched || previewFetching) return { allowed: false, reason: 'search-first' };
  if (preview && preview.eligible_contacts === 0) return { allowed: false, reason: 'none-addable' };
  return { allowed: true };
}

/**
 * The empty-table message in Add Contacts. Picking different tags clears the
 * last Search, so the list waits for a new one instead of showing contacts
 * that may not match the current tags.
 */
export function contactListPlaceholder({
  searched,
  hasTags,
  isFetching,
}: {
  searched: boolean;
  hasTags: boolean;
  isFetching: boolean;
}): string {
  if (!searched) {
    return hasTags
      ? 'Click Search to update the contacts for these tags'
      : 'Select tags and click Search to see the contacts';
  }
  return isFetching ? 'Loading contacts…' : 'No contacts found.';
}

const plural = (n: number, one: string, many: string) =>
  `${formatNumber(n)} ${n === 1 ? one : many}`;

/** The toast after Add to Flow, from what the bulk add actually did. */
export function bulkAddToast(result: BulkAddResult): {
  message: string;
  variant: 'success' | 'info' | 'error';
} {
  if (!result.success) {
    return { message: result.error || 'Failed to add contacts to flow', variant: 'error' };
  }
  const created = result.created ?? 0;
  const skipped = result.skipped_duplicate ?? 0;
  const errors = result.errors ?? 0;
  const leftOut = excludedTotal(result.excluded);

  if (errors > 0 && created === 0) {
    return {
      message: `Failed to add ${plural(errors, 'contact', 'contacts')} to flow`,
      variant: 'error',
    };
  }

  const parts: string[] = [];
  if (created > 0) parts.push(`Added ${plural(created, 'contact', 'contacts')}`);
  if (skipped > 0) parts.push(`${formatNumber(skipped)} already in flow`);
  if (leftOut > 0) {
    const top = excludedReasons(result.excluded)[0];
    const reason = top ? top.label[0].toLowerCase() + top.label.slice(1) : '';
    parts.push(
      `${formatNumber(leftOut)} left out${reason ? ` (${reason}${leftOut > top.count ? ', …' : ''})` : ''}`
    );
  }
  if (errors > 0) parts.push(`${formatNumber(errors)} failed`);

  if (parts.length === 0) {
    return { message: 'No matching contacts found for the selected tags', variant: 'info' };
  }
  if (created === 0) parts.unshift('No new contacts added');
  return { message: parts.join(' · '), variant: created > 0 ? 'success' : 'info' };
}
